import React, { useState, useEffect } from 'react';
import '../Style/login.css';
import { RANK_IMAGES } from '../utils/rankConstants';
import apiClient from '../api/client';
import { sendLoginCode, verifyLoginCode } from '../api/authApi';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

function Login() {
  const navigate = useNavigate();
  const { setCurrentUser } = useContext(AuthContext);

  const [randomEmblem, setRandomEmblem] = useState(RANK_IMAGES.DIAMOND);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email'); // email | code
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const keys = Object.keys(RANK_IMAGES);
    const randomKey = keys[Math.floor(Math.random() * keys.length)];
    setRandomEmblem(RANK_IMAGES[randomKey]);
  }, []);

  const handleGoogleLogin = () => {
    window.location.href = `${apiClient.defaults.baseURL}/oauth2/authorization/google`;
  };

  const handleSendCode = async () => {
    setError('');
    setSuccessMessage('');

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);

    try {
      await sendLoginCode(cleanEmail);
      setEmail(cleanEmail);
      setStep('code');
      setSuccessMessage('Code sent. Check your inbox or spam folder.');
    } catch (err) {
      console.error('Send code error:', err);
      setError(err.response?.data?.error || 'Could not send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');

    const cleanEmail = email.trim().toLowerCase();
    const cleanCode = code.trim();

    if (cleanCode.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }

    setLoading(true);

    try {
      const verifyRes = await verifyLoginCode(cleanEmail, cleanCode);
      const sessionToken = verifyRes.token;

      localStorage.setItem('X-Auth-Token', sessionToken);
      apiClient.defaults.headers.common['X-Auth-Token'] = sessionToken;

      const userRes = await apiClient.get('/users/me');
      const user = userRes.data;

      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));

      navigate(user.profileCompleted ? '/' : '/complete-profile', { replace: true });
    } catch (err) {
      console.error('Verify code error:', err);
      setError(err.response?.data?.error || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="login-page">
      <div className="login-bg-decoration">
        <img src={RANK_IMAGES.IRON} className="bg-icon iron" alt="" />
        <img src={RANK_IMAGES.GOLD} className="bg-icon gold" alt="" />
      </div>

      <div className="login-card glass-card">
        <div className="login-header">
          <div className="brand-logo">
            <img src={randomEmblem} alt="Global Legends" className="main-logo-img" />
          </div>
          <h1>Global Legends</h1>
          <p>Track your goals and stay consistent.</p>
        </div>

        <div className="login-actions">
          <button
            onClick={handleGoogleLogin}
            className="google-login-btn"
            data-testid="login-button"
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
            />
            <span>Continue with Google</span>
          </button>

          <div className="magic-link-section">
            <div className="divider">
              <span>or</span>
            </div>

            {step === 'email' && (
              <>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="magic-link-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                />

                <button onClick={handleSendCode} className="magic-link-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Code'}
                </button>
              </>
            )}

            {step === 'code' && (
              <div className="email-code-section">
                <p className="code-help-text">
                  Check your inbox or spam folder. Codes expire after 10 minutes.
                </p>

                <input
                  type="text"
                  inputMode="numeric"
                  maxLength="6"
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCode(value);
                  }}
                  className="magic-link-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                />

                <button onClick={handleVerifyCode} className="magic-link-btn" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>

                <button
                  type="button"
                  onClick={handleSendCode}
                  className="try-again-btn"
                  disabled={loading}
                >
                  Resend code
                </button>

                <button
                  type="button"
                  onClick={handleUseDifferentEmail}
                  className="try-again-btn"
                  disabled={loading}
                >
                  Use a different email
                </button>
              </div>
            )}

            {successMessage && <p className="login-success-message">{successMessage}</p>}
            {error && <p className="login-error-message">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
